import { Box, FormControl, Text, useDisclosure } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import * as React from 'react';
import Button from '~/components/Button';
import DateRangePicker from '~/components/DateRangePicker';
import FormLabel from '~/components/FormLabel/FormLabel';
import LinkButton from '~/components/LinkButton';
import Modal from '~/components/Modal/Modal';
import Select from '~/components/Select';
import Toggle from '~/components/Toggle';

import { FiltersIcon } from '../icons';
import { RecentContentFiltersBarTagSelect } from './components';
import { datePickerControllerProps, datePickerProps } from './utils';

const propTypes = {
  sortByValue: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  onSortByChange: PropTypes.func.isRequired,
  sortingOptions: PropTypes.array,
  adFilterValue: PropTypes.bool,
  onAdFilterChange: PropTypes.func,
  tagsValue: PropTypes.arrayOf(PropTypes.string),
  onTagsChange: PropTypes.func,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  onDatesChange: PropTypes.func.isRequired,
};

const SectionHeader = (props) => (
  <Text
    textTransform="uppercase"
    fontSize="sm"
    color="gray.800"
    marginBottom={2}
    fontWeight="bold"
    letterSpacing="1px"
    {...props}
  />
);

const MobileFiltersBar = ({
  sortByValue: initialSortBy,
  onSortByChange: setInitialSortBy,
  sortingOptions,
  adFilterValue: initialAdFilter,
  onAdFilterChange: setInitialAdFilter,
  tagsValue: initialTags,
  onTagsChange: setInitialTags,
  startDate: initialStartDate,
  endDate: initialEndDate,
  onDatesChange: setInitialDates,
  ...props
}) => {
  const isSubmitting = false;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [sortBy, setSortBy] = React.useState(initialSortBy);
  const [adFilter, setAdFilter] = React.useState(initialAdFilter);
  const [tags, setTags] = React.useState(initialTags);
  const [startDate, setStartDate] = React.useState();
  const [endDate, setEndDate] = React.useState();

  const handleSubmitClick = () => {
    if (sortBy !== initialSortBy) {
      setInitialSortBy(sortBy);
    }
    if (adFilter !== initialAdFilter) {
      setInitialAdFilter(adFilter);
    }
    if (tags !== initialTags) {
      setInitialTags(tags);
    }
    if (startDate !== initialStartDate || endDate !== initialEndDate) {
      setInitialDates({ startDate, endDate });
    }
    onClose();
  };

  const closeAndReset = () => {
    setSortBy(initialSortBy);
    setAdFilter(initialAdFilter);
    setTags(initialTags);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    onClose();
  };

  const clearFilters = () => {
    setInitialAdFilter && setInitialAdFilter(false);
    setInitialTags && setInitialTags([]);
    setInitialDates({ startDate: undefined, endDate: undefined });
    onClose();
  };

  const handleDateChange = (newValues) => {
    setStartDate(newValues.startDate);
    setEndDate(newValues.endDate);
  };

  React.useEffect(() => {
    setSortBy(initialSortBy);
    setAdFilter(initialAdFilter);
    setTags(initialTags);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [
    initialSortBy,
    initialAdFilter,
    initialTags,
    initialStartDate,
    initialEndDate,
  ]);

  const hasFilterSet =
    initialAdFilter ||
    initialTags?.length > 0 ||
    !!initialStartDate ||
    !!initialEndDate;

  return (
    <>
      <Button
        textTransform="initial"
        backgroundColor="white"
        borderColor="gray.200"
        onClick={onOpen}
        color="gray.800"
        fontSize="md"
        fontWeight="normal"
        letterSpacing="0"
        {...props}
      >
        <FiltersIcon
          marginRight="1"
          boxSize={6}
          color="gray.800"
          fill="gray.800"
        />
        {hasFilterSet && (
          <Box
            backgroundColor="error.500"
            width="8px"
            height="8px"
            borderRadius="50%"
            pos="absolute"
            top="7px"
            left="32px"
          />
        )}
        Sort | Filters
      </Button>
      <Modal isOpen={isOpen} onClose={closeAndReset} size="lg">
        <Modal.Header paddingTop={8} />
        <Modal.CloseButton
          // hack due to how have done RTL tests...
          onClick={closeAndReset}
        />
        <Modal.Body paddingX={5} overflowY="auto">
          <Box marginBottom={10}>
            <SectionHeader>Sorting</SectionHeader>
            <Select
              options={sortingOptions}
              value={sortBy}
              onChange={setSortBy}
              aria-label="Set order"
              isSearchable={false}
            />
          </Box>
          <Box>
            <SectionHeader>Filtering</SectionHeader>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onDatesChange={handleDateChange}
              marginBottom={4}
              controllerProps={datePickerControllerProps}
              width="100%"
              {...datePickerProps}
            />
            {setInitialTags && (
              <RecentContentFiltersBarTagSelect
                tags={tags}
                onChange={setTags}
                marginBottom={2}
              />
            )}
            {setInitialAdFilter && (
              <FormControl>
                <Toggle
                  id="ad-toggle-mobile"
                  marginRight={2}
                  onChange={() => setAdFilter(!adFilter)}
                  isChecked={adFilter}
                />
                <FormLabel
                  fontSize="md"
                  color="gray.800"
                  htmlFor="ad-toggle-mobile"
                  verticalAlign="baseline"
                >
                  Includes #ad
                </FormLabel>
              </FormControl>
            )}
          </Box>
        </Modal.Body>
        <Modal.Footer spaceBetween flexDirection="row-reverse">
          <Button
            colorScheme="primary"
            onClick={handleSubmitClick}
            isLoading={isSubmitting}
          >
            Apply
          </Button>
          {hasFilterSet && (
            <LinkButton color="black" letterSpacing="0" onClick={clearFilters}>
              Clear all
            </LinkButton>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

MobileFiltersBar.propTypes = propTypes;

export default MobileFiltersBar;
